import { Injectable } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ActivatedRouterStateSnapshot,
  CurrencyService,
  LanguageService,
  ProductSearchService,
  RoutingService
} from '@spartacus/core'
import {
  ProductListComponentService,
  ProductListRouteParams,
  SearchCriteria,
  ViewConfig
} from '@spartacus/storefront'
import { combineLatest, Observable, Subscription } from 'rxjs'
import { debounceTime, distinctUntilChanged, map, take, tap } from 'rxjs/operators'
import { EbUnitSelectorService } from 'src/app/components/unit-selector/eb-unit-selector.service'
import { EbBaseSiteService } from '../../../site-context/services/eb-base-site.service'

@Injectable({ providedIn: 'root' })
export class EbProductListComponentService extends ProductListComponentService {
  searchByRouting$: Observable<ActivatedRouterStateSnapshot> = combineLatest([
    this.routing.getRouterState().pipe(
      distinctUntilChanged((x, y) => {
        // router emits new value also when the anticipated `nextState` changes
        // but we want to perform search only when current url changes
        return x.state.url === y.state.url
      })
    ),
    ...this.context
  ]).pipe(
    debounceTime(0),
    map(([routerState, ..._context]) => routerState.state),
    tap((state: ActivatedRouterStateSnapshot) => {
      this.searchBasedOnCriteria(state)
    })
  )
  constructor(
    protected productSearchService: ProductSearchService,
    protected routing: RoutingService,
    protected activatedRoute: ActivatedRoute,
    protected currencyService: CurrencyService,
    protected languageService: LanguageService,
    protected baseSiteService: EbBaseSiteService,
    protected router: Router,
    protected config: ViewConfig,
    protected ebUnitSelectorService: EbUnitSelectorService
  ) {
    super(
      productSearchService,
      routing,
      activatedRoute,
      currencyService,
      languageService,
      router,
      config
    )
  }

  // we need to overwrite super class to handle baseSite and language but needed context is private
  private get context(): Observable<string>[] {
    return [this.languageService.getActive(), this.baseSiteService.getActive()]
  }

  /**
   * Get items from a given page without using navigation
   */
  getPageItems(pageNumber: number): void {
    this.routing
      .getRouterState()
      .pipe(take(1))
      .subscribe(route => {
        let routeCriteria: SearchCriteria
        if (route.state.params.query || route.state.url.includes('/c')) {
          routeCriteria = this.getCriteriaFromRoute(route.state.params, route.state.queryParams)
        } else {
          const query = {
            query: decodeURIComponent(route.state.url.split('/').pop())
          } as ProductListRouteParams
          routeCriteria = this.getCriteriaFromRoute(query, route.state.queryParams)
        }
        const criteria = {
          ...routeCriteria,
          currentPage: pageNumber
        }
        this.search(criteria)
      })
  }

  triggerSearchOnUnitChange(): Subscription {
    return this.ebUnitSelectorService.getUnitChanged()
      .subscribe((changed) => {
        if (changed) {
          this.routing
            .getRouterState()
            .pipe(take(1))
            .subscribe(route => {
              this.searchBasedOnCriteria(route.state)
            })
        }
      })
  }

  private searchBasedOnCriteria(state: ActivatedRouterStateSnapshot) {
    let criteria: SearchCriteria
    if (state.params.query || state.url.includes('/c')) {
      criteria = this.getCriteriaFromRoute(state.params, state.queryParams)
    } else {
      const query = {
        query: decodeURIComponent(state.url.split('/').pop())
      } as ProductListRouteParams
      criteria = this.getCriteriaFromRoute(query, state.queryParams)
    }
    this.search(criteria)
  }

  protected getQueryFromRouteParams({
    query,
    categoryCode,
    brandCode,
  }: ProductListRouteParams) {
    if (query) {
      return query;
    }
    if (categoryCode) {
      return "::allCategories:" + categoryCode;
    }

    if (brandCode) {
      return "::allCategories:" + brandCode;
    }
  }
}
